













import Vue from "vue";

import { TimerHandler } from "../../utils/time";
import { TimerColorCfg, TimerThresholdHandler } from "../../models/time";

interface Props {
  // Initial remaining time in seconds
  seconds: number;
  // The text to display before the remaining time
  text: string;
  // The period, in milliseconds, to update the timer. Default is 100ms
  timerPeriod: number;
  // if true, immediately start the timer. Default is true
  startOnCreated: boolean;
  // Dynamically change the progress bar colour. Threshold are assumed
  // to be properly ordered
  dynamicColors: TimerColorCfg[];
}

interface State {
  timerHandler: TimerHandler;
  // To monitor the next dynamic colour
  dynamicColor?: string;
}

interface Computed {
  // Return the dynamic style for progress bar
  timerProgressStyle: { [key: string]: string };
}

interface Methods {
  // (Re-)initialise all values
  reset: () => void;
  // Start timer
  start: () => void;
  // Stop the timer
  stop: () => void;
}

const DEFAULT_DYNAMIC_COLOR: TimerColorCfg[] = [
  { color: "var(--color-danger", threshold: 5000, type: "milliseconds" }
];

/**
 * decreaseTimer period is far from being optimized but easier to read
 *
 * @emit timer-start
 * @emit timer-stopped
 * @emit timer-finished
 */
export default Vue.extend<State, Methods, Computed, Props>({
  name: "JzTimerBar",

  props: {
    seconds: { type: Number, required: true },
    text: { type: String, default: "残り時間" },
    timerPeriod: { type: Number, default: 100 },
    startOnCreated: { type: Boolean, default: true },
    dynamicColors: { type: Array, default: () => DEFAULT_DYNAMIC_COLOR }
  },

  data() {
    return {
      timerHandler: new TimerHandler({
        seconds: this.seconds,
        timerPeriod: this.timerPeriod,
        onTimerStarted: () => {
          this.$emit("timer-start");
        },
        onTimerStopped: () => {
          this.$emit("timer-stopped");
        },
        onTimerFinished: () => {
          this.$emit("timer-finished");
        },
        thresholdHandlers: this.dynamicColors.map(
          (dynCol: TimerColorCfg): TimerThresholdHandler => ({
            threshold: dynCol.threshold,
            type: dynCol.type,
            callback: () => {
              // TypeScript: other data not available for auto-completion
              this.$data["dynamicColor"] = dynCol.color;
            }
          })
        )
      }),
      dynamicColor: undefined
    };
  },

  computed: {
    timerProgressStyle() {
      const styles: { [key: string]: string } = this.dynamicColor
        ? {
            width: `${this.timerHandler.remainingPercentage}%`,
            "background-color": this.dynamicColor
          }
        : { width: `${this.timerHandler.remainingPercentage}%` };
      return styles;
    }
  },

  created() {
    if (this.startOnCreated) this.start();
  },

  destroyed() {
    // To avoid infinite interval in the background
    this.timerHandler.clear();
  },

  methods: {
    reset() {
      this.timerHandler.reset({
        seconds: this.seconds,
        timerPeriod: this.timerPeriod,
        thresholdHandlers: this.dynamicColors.map(
          (dynCol: TimerColorCfg): TimerThresholdHandler => ({
            threshold: dynCol.threshold,
            type: dynCol.type,
            callback: () => {
              this.dynamicColor = dynCol.color;
            }
          })
        )
      });
    },

    start() {
      this.timerHandler.start();
    },

    stop() {
      this.timerHandler.stop();
    }
  }
});
